import { useState, useRef, useCallback, useEffect } from 'react';
import { X, Upload, Loader2, CheckCircle, AlertTriangle, FileText } from 'lucide-react';
import { useAnalyzeQuote, useConfirmQuoteImport } from '../../hooks/useQuoteImport';
import { useExpenseConfig } from '../../hooks/useExpenses';
import { useVendorCategories } from '../../hooks/useVendors';
import { getPresignedUploadUrl, uploadFileToS3 } from '../../api/photos';
import type { QuoteAnalysisResult, QuoteImportItem } from '../../api/quote-import';

const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp', 'image/tiff'];
const MAX_SIZE = 25 * 1024 * 1024;

type Step = 'upload' | 'analyzing' | 'review';

interface InvoiceImportModalProps {
  eventId: string;
  isOpen: boolean;
  onClose: () => void;
  onImported: () => void;
}

interface ReviewItem extends QuoteImportItem {
  index: number;
}

function formatCurrency(amount: number): string {
  return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function confidenceBadge(confidence: number) {
  if (confidence >= 0.8) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
        <CheckCircle size={12} />
        High confidence ({Math.round(confidence * 100)}%)
      </span>
    );
  }
  if (confidence >= 0.5) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
        <AlertTriangle size={12} />
        Medium confidence ({Math.round(confidence * 100)}%)
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
      <AlertTriangle size={12} />
      Low confidence ({Math.round(confidence * 100)}%)
    </span>
  );
}

export function InvoiceImportModal({
  eventId,
  isOpen,
  onClose,
  onImported,
}: InvoiceImportModalProps) {
  const [step, setStep] = useState<Step>('upload');
  const [dragActive, setDragActive] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const [documentId, setDocumentId] = useState('');
  const [analysis, setAnalysis] = useState<QuoteAnalysisResult | null>(null);
  const [vendorName, setVendorName] = useState('');
  const [reviewItems, setReviewItems] = useState<ReviewItem[]>([]);
  const [importError, setImportError] = useState('');

  const analyzeQuote = useAnalyzeQuote();
  const confirmImport = useConfirmQuoteImport();
  const { data: _config } = useExpenseConfig();
  void _config;
  const { data: categories = [] } = useVendorCategories();

  useEffect(() => {
    if (!isOpen) {
      setStep('upload');
      setDragActive(false);
      setUploadError('');
      setSelectedFile(null);
      setIsUploading(false);
      setDocumentId('');
      setAnalysis(null);
      setVendorName('');
      setReviewItems([]);
      setImportError('');
      analyzeQuote.reset();
      confirmImport.reset();
    }
  }, [isOpen]);

  const validateFile = useCallback((file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return `"${file.name}" is not a supported file type. Please upload a PDF or image.`;
    }
    if (file.size > MAX_SIZE) {
      return `"${file.name}" exceeds the 25 MB limit.`;
    }
    return null;
  }, []);

  const handleFileSelected = useCallback(
    (file: File) => {
      const error = validateFile(file);
      if (error) {
        setUploadError(error);
        return;
      }
      setUploadError('');
      setSelectedFile(file);
    },
    [validateFile],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragActive(false);
      if (e.dataTransfer.files.length > 0) {
        handleFileSelected(e.dataTransfer.files[0]);
      }
    },
    [handleFileSelected],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        handleFileSelected(e.target.files[0]);
        e.target.value = '';
      }
    },
    [handleFileSelected],
  );

  const handleAnalyze = useCallback(async () => {
    if (!selectedFile) return;

    setStep('analyzing');
    setUploadError('');

    try {
      setIsUploading(true);
      const presigned = await getPresignedUploadUrl({
        fileName: selectedFile.name,
        mimeType: selectedFile.type,
      });
      await uploadFileToS3(presigned.uploadUrl, selectedFile);
      setIsUploading(false);

      const result = await analyzeQuote.mutateAsync({
        eventId,
        s3Key: presigned.s3Key,
        fileName: selectedFile.name,
        mimeType: selectedFile.type,
        fileSizeBytes: selectedFile.size,
      });

      setDocumentId(result.documentId);
      setAnalysis(result.analysis);
      setVendorName(result.analysis.vendorName || '');
      setReviewItems(
        result.analysis.lineItems.map((item, index) => ({
          ...item,
          accepted: true,
          index,
        })),
      );
      setStep('review');
    } catch (err) {
      setIsUploading(false);
      setStep('upload');
      setUploadError(
        err instanceof Error ? err.message : 'Failed to analyze invoice. Please try again.',
      );
    }
  }, [selectedFile, eventId, analyzeQuote]);

  const handleToggleItem = useCallback((index: number) => {
    setReviewItems((prev) =>
      prev.map((item) => (item.index === index ? { ...item, accepted: !item.accepted } : item)),
    );
  }, []);

  const handleToggleAll = useCallback(() => {
    setReviewItems((prev) => {
      const allAccepted = prev.every((item) => item.accepted);
      return prev.map((item) => ({ ...item, accepted: !allAccepted }));
    });
  }, []);

  const handleItemChange = useCallback(
    (index: number, field: keyof ReviewItem, value: string | number) => {
      setReviewItems((prev) =>
        prev.map((item) => {
          if (item.index !== index) return item;
          const updated = { ...item, [field]: value };
          if (field === 'quantity' || field === 'unitPrice') {
            updated.totalPrice =
              Number(field === 'quantity' ? value : item.quantity) *
              Number(field === 'unitPrice' ? value : item.unitPrice);
          }
          return updated;
        }),
      );
    },
    [],
  );

  const handleImport = useCallback(async () => {
    setImportError('');
    const acceptedItems = reviewItems.filter((item) => item.accepted);
    if (acceptedItems.length === 0) {
      setImportError('Please select at least one item to import.');
      return;
    }

    try {
      await confirmImport.mutateAsync({
        eventId,
        documentId,
        vendorName: vendorName || undefined,
        items: acceptedItems.map(({ index: _index, ...rest }) => rest),
      });
      onImported();
      onClose();
    } catch (err) {
      setImportError(
        err instanceof Error ? err.message : 'Failed to import items. Please try again.',
      );
    }
  }, [reviewItems, eventId, documentId, vendorName, confirmImport, onImported, onClose]);

  const selectedTotal = reviewItems
    .filter((item) => item.accepted)
    .reduce((sum, item) => sum + item.totalPrice, 0);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      <div className="relative w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-3xl sm:mx-4 bg-white sm:rounded-lg shadow-xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">Import from Invoice</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-4 sm:p-6">
          {step === 'upload' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Upload a vendor invoice (PDF or image) and our AI will extract the line items for
                you to review and import into your expense tracker.
              </p>

              <div
                onDrop={handleDrop}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragActive(true);
                }}
                onDragLeave={() => setDragActive(false)}
                onClick={() => inputRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  dragActive
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
                }`}
                data-testid="invoice-dropzone"
              >
                <Upload className="mx-auto mb-3 text-gray-400" size={32} />
                <p className="text-sm text-gray-600">
                  Drag & drop your invoice here, or{' '}
                  <span className="text-primary-600 font-medium">browse</span>
                </p>
                <p className="text-xs text-gray-400 mt-1">PDF, JPEG, PNG, WebP, TIFF up to 25 MB</p>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".pdf,application/pdf,image/jpeg,image/png,image/webp,image/tiff"
                  onChange={handleInputChange}
                  className="hidden"
                  data-testid="invoice-file-input"
                />
              </div>

              {selectedFile && (
                <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-lg">
                  <FileText size={20} className="text-gray-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {selectedFile.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {(selectedFile.size / 1024 / 1024).toFixed(1)} MB
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                    }}
                    className="text-gray-400 hover:text-gray-600 p-1"
                    aria-label="Remove file"
                  >
                    <X size={16} />
                  </button>
                </div>
              )}

              {uploadError && <p className="text-sm text-red-600">{uploadError}</p>}
            </div>
          )}

          {step === 'analyzing' && (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <Loader2 size={40} className="text-primary-600 animate-spin" />
              <p className="text-sm text-gray-600">
                {isUploading ? 'Uploading invoice...' : 'Analyzing your invoice with AI...'}
              </p>
              <p className="text-xs text-gray-400">This may take a few moments</p>
            </div>
          )}

          {step === 'review' && analysis && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">Extracted Data</h3>
                {confidenceBadge(analysis.confidence)}
              </div>

              {/* Vendor info */}
              <div>
                <label
                  htmlFor="invoice-vendor-name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Vendor Name
                </label>
                <input
                  id="invoice-vendor-name"
                  type="text"
                  value={vendorName}
                  onChange={(e) => setVendorName(e.target.value)}
                  placeholder="Vendor name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {(analysis.paymentTerms || analysis.dueDate) && (
                <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                  {analysis.paymentTerms && (
                    <span>
                      <span className="font-medium text-gray-700">Payment Terms:</span>{' '}
                      {analysis.paymentTerms}
                    </span>
                  )}
                  {analysis.dueDate && (
                    <span>
                      <span className="font-medium text-gray-700">Due Date:</span>{' '}
                      {analysis.dueDate}
                    </span>
                  )}
                </div>
              )}

              {/* Line items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-700">
                    Line Items ({reviewItems.filter((i) => i.accepted).length} of{' '}
                    {reviewItems.length} selected)
                  </h4>
                  <button
                    onClick={handleToggleAll}
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                  >
                    {reviewItems.every((i) => i.accepted) ? 'Deselect All' : 'Select All'}
                  </button>
                </div>

                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left w-10">
                          <input
                            type="checkbox"
                            checked={reviewItems.every((i) => i.accepted)}
                            onChange={handleToggleAll}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">
                          Description
                        </th>
                        <th className="px-3 py-2 text-right font-medium text-gray-700 w-20">Qty</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-700 w-28">
                          Unit Price
                        </th>
                        <th className="px-3 py-2 text-right font-medium text-gray-700 w-28">
                          Total
                        </th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700 w-36">
                          Category
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {reviewItems.map((item) => (
                        <tr
                          key={item.index}
                          className={item.accepted ? 'bg-white' : 'bg-gray-50 opacity-60'}
                        >
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={item.accepted}
                              onChange={() => handleToggleItem(item.index)}
                              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                          </td>
                          <td className="px-3 py-2 text-gray-900">{item.description}</td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              value={item.quantity}
                              onChange={(e) =>
                                handleItemChange(
                                  item.index,
                                  'quantity',
                                  parseFloat(e.target.value) || 0,
                                )
                              }
                              min="0"
                              step="1"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              value={item.unitPrice}
                              onChange={(e) =>
                                handleItemChange(
                                  item.index,
                                  'unitPrice',
                                  parseFloat(e.target.value) || 0,
                                )
                              }
                              min="0"
                              step="0.01"
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-right focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                          </td>
                          <td className="px-3 py-2 text-right text-gray-900 font-medium">
                            {formatCurrency(item.totalPrice)}
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={item.vendorCategoryId || ''}
                              onChange={(e) =>
                                handleItemChange(
                                  item.index,
                                  'vendorCategoryId' as keyof ReviewItem,
                                  e.target.value,
                                )
                              }
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            >
                              <option value="">Select...</option>
                              {categories.map((cat) => (
                                <option key={cat.id} value={cat.id}>
                                  {cat.name}
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg">
                <span className="text-sm font-medium text-gray-700">Selected Total</span>
                <span className="text-lg font-semibold text-gray-900">
                  {formatCurrency(selectedTotal)}
                </span>
              </div>

              {importError && <p className="text-sm text-red-600">{importError}</p>}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-4 py-3 sm:px-6 sm:py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>

          {step === 'upload' && (
            <button
              onClick={handleAnalyze}
              disabled={!selectedFile}
              className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Analyze Invoice
            </button>
          )}

          {step === 'review' && (
            <button
              onClick={handleImport}
              disabled={
                confirmImport.isPending || reviewItems.filter((i) => i.accepted).length === 0
              }
              className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {confirmImport.isPending
                ? 'Importing...'
                : `Import Selected (${reviewItems.filter((i) => i.accepted).length})`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
